import { RootState } from '..'
import { createSelector } from 'reselect'
import moment from 'moment'
import { IEvent, WorkflowStatus } from './interfaces'
import { FilterState } from '../../components/events/EventFilter'

function matchingAccessibility(filter: Partial<FilterState>, event: IEvent) {
  const selected = filter.supportedDisabilities?.map((d) => d.value) || []
  const eventAccessibility: string[] = event.supported_disabilities.split(",")
  const matched: string[] = []

  selected.forEach((value) => {
    if (eventAccessibility.includes(value)) {
      matched.push(value)
    }
  })
  return matched
}

export const getFilteredEvents = createSelector(
  (state: RootState) => state.events.filter,
  (state: RootState) => state.events.list,
  (state: RootState) => state.registrations.list,
  (state: RootState) => state.program.data,
  (state: RootState) => state.institution.testMode,
  (filter, events, registrations, program, testMode) =>
    events
      .filter(
        (event) =>
          // Filter for workflow state
          (!program.use_workflow ||
            (program.use_workflow && event.workflow_status === WorkflowStatus.INSTITUTION_OK) ||
            testMode) &&
          // Filter out past events, if setting is on
          (!program.hide_past_events || new Date(event.end) >= new Date() || testMode) &&
          // Filter for keyword
          (!filter.keyword ||
            event.name.toLowerCase().includes(filter.keyword.toLowerCase()) ||
            event.relative_id.toString().includes(filter.keyword)) &&
          // Filter for date range
          (!Object.prototype.hasOwnProperty.call(event, 'timespans') ||
            event.timespans?.length === 0 ||
            event.timespans?.some(
              (ts) =>
                (!filter.start ||
                  !ts.start ||
                  moment(ts.start) >= moment(filter.start, 'DD.MM.YYYY')) &&
                (!filter.end ||
                  !ts.end ||
                  moment(ts.end) <= moment(filter.end, 'DD.MM.YYYY'))
            )) &&
          // Filter for age
          (!filter.age ||
            ((!event.min_age || event.min_age <= filter.age) &&
              (!event.max_age || event.max_age >= filter.age))) &&
          // Filter for adults only
          (!filter.adultsOnly || event.is_only_for_adults) &&
          // Filter for accessibility
          (!filter.supportedDisabilities ||
            !filter.supportedDisabilities.length ||
            matchingAccessibility(filter, event).length > 0) &&
          (!filter.uncanceledOnly || !event.canceled) &&
          // Filter for premarked
          (!filter.premarkedOnly ||
            registrations.some(
              (reg) => reg.event.event_id === event.event_id
            )) &&
          // Filter for occupation level and gender
          (!filter.notFullOnly ||
            (event.registration_statistics.event_occupation_bar_data.total_per <
              1 &&
              (filter.gender === 'e' ||
                (filter.gender === 'm' &&
                  event.registration_statistics.event_occupation_bar_data
                    .male_per < 1) ||
                (filter.gender === 'w' &&
                  event.registration_statistics.event_occupation_bar_data
                    .female_per < 1) ||
                (filter.gender === 'd' &&
                  event.registration_statistics.event_occupation_bar_data
                    .diverse_per < 1)))) &&
          // Filter for gender
          (!filter.gender ||
            filter.gender === 'e' ||
            event.allowed_genders.includes(filter.gender)) &&
          // Filter for tags
          (!filter.tags ||
            filter.tags.length === 0 ||
            filter.tags.filter((value) => event.tags.includes(value.label))
              .length)
      )
      // Sort by starting date
      .sort((A, B) => (A.start || '9').localeCompare(B.start || '9'))
)

export const getRegisteredEvents = createSelector(
  (state: RootState) => state.registrations.list,
  (registrations) =>
    registrations
      .slice()
      .sort((A, B) => A.priority - B.priority)
      .map((reg) => reg.event)
)

export const selectHasPaidEvents = createSelector(
  (state: RootState) => state.events.list,
  (events) => events.some((event) => {
    const basePrice = event.price ?? 0
    const customPrice = event.custom_price ?? 0
    const categoryHasPaidPrice =
      Array.isArray(event.price_categories) &&
      event.price_categories.some((c) => (c.price ?? 0) > 0)

    return basePrice > 0 || customPrice > 0 || categoryHasPaidPrice
  })
)
